import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, Utensils, Camera, ShieldCheck, TrendingUp, MessageCircle, ArrowRight } from 'lucide-react';
import SEO, { softwareAppJsonLd, breadcrumbJsonLd } from '../components/SEO';
import { trackEvent } from '../lib/analytics';
import Header from '../components/Header';
import Footer from '../components/Footer';

const STEPS = [
  {
    n: '01',
    icon: Activity,
    title: 'Tell it who you are today',
    body: "One short intake: your goal, injuries you're carrying, equipment you actually own, the days you can realistically train, and where your energy sits this week. No questionnaires that punish you for skipping a session.",
  },
  {
    n: '02',
    icon: Utensils,
    title: 'A plan sized to your life',
    body: "The engine generates a week of workouts + meals to hit your macros, priced against your UK supermarket of choice. Aisle-sorted shopping list, one-tap logging, no cabbage soup.",
  },
  {
    n: '03',
    icon: ShieldCheck,
    title: 'Injuries route the plan — deterministically',
    body: "Log a shoulder or knee flare and every future workout auto-swaps contraindicated exercises for joint-friendly alternates that still train the same muscle. Not an LLM guess — a hard rules pass backed by clinical data. Every swap is explained.",
  },
  {
    n: '04',
    icon: Camera,
    title: 'Log without friction',
    body: "Take a photo of any meal → real macros in seconds, oil-and-dressing-aware, with a confidence score. Skip the barcode scanner. Skip the food database. Adjust anything the AI got wrong.",
  },
  {
    n: '05',
    icon: TrendingUp,
    title: 'Progression on rails',
    body: "The overload tracker watches your working sets, RPE and volume, and nudges load, reps or tempo up only when the data says you're ready. Auto-deload weeks when fatigue accumulates. No missed months from grinding.",
  },
  {
    n: '06',
    icon: MessageCircle,
    title: 'Ask anything, get a coach answer',
    body: "Chat to an AI coach that already knows your goal, your kit, your recent training and your injuries. Grounded in your plan, evidence-first, zero judgement.",
  },
];

const RELOOP = [
  { h: 'Every session', p: 'Log the work + how it felt. The engine reads recovery, effort and completion.' },
  { h: 'Every week', p: 'The plan regenerates — volume, exercise selection and macros nudge to the version of you the data shows up as.' },
  { h: 'Every 14 days', p: 'A quick check-in on any active injuries. Cleared? We restore the original exercises automatically. Still healing? We keep protecting you.' },
];

export default function HowItWorks() {
  useEffect(() => { trackEvent('how_it_works_view'); }, []);
  return (
    <div className="min-h-screen bg-[#0b0b0c] text-white">
      <SEO
        title="How it works"
        description="How FreeFitFuel adapts to you: a six-step loop of intake, planning, injury-safe adaptation, effortless logging, data-driven progression, and an AI coach that knows your plan."
        path="/how-it-works"
        keywords="how freefitfuel works, adaptive fitness app, ai fitness planning, personalised workout system"
        jsonLd={{
          '@context': 'https://schema.org',
          '@graph': [
            softwareAppJsonLd({
              name: 'FreeFitFuel — How it works',
              description: 'A six-step adaptive loop: intake, plan, injury-safe, log, progress, coach.',
              url: 'https://freefitfuel.app/how-it-works',
            }),
            breadcrumbJsonLd([
              { name: 'Home', path: '/' },
              { name: 'How it works', path: '/how-it-works' },
            ]),
            {
              '@context': 'https://schema.org',
              '@type': 'HowTo',
              name: 'How FreeFitFuel adapts to you',
              description: 'Six-step system that regenerates your training and nutrition every week based on your data.',
              step: STEPS.map((s) => ({
                '@type': 'HowToStep',
                position: parseInt(s.n, 10),
                name: s.title,
                text: s.body,
              })),
            },
          ],
        }}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="max-w-3xl mx-auto px-5 pt-16 sm:pt-24 pb-8">
          <p className="text-[11px] uppercase tracking-[0.2em] font-black text-[#BEFF38]">How it works</p>
          <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
            One adaptive loop. <span className="text-[#ff7a1a]">Six moving parts.</span>
          </h1>
          <p className="mt-4 text-xl text-zinc-300 leading-relaxed">
            Every week your plan regenerates around the version of you the data actually shows up as — injuries, energy, equipment, results.
          </p>
        </section>

        {/* Steps */}
        <section className="max-w-3xl mx-auto px-5 pb-16">
          <ol className="space-y-4">
            {STEPS.map(({ n, icon: Icon, title, body }) => (
              <li
                key={n}
                data-testid={`hiw-step-${n}`}
                className="rounded-2xl border border-zinc-800 bg-zinc-950 p-5 sm:p-6 hover:border-[#BEFF38]/40 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="shrink-0 w-11 h-11 rounded-xl bg-[#BEFF38]/10 border border-[#BEFF38]/30 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#BEFF38]" strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3">
                      <span className="text-[11px] font-black text-[#ff7a1a] tracking-wider">STEP {n}</span>
                    </div>
                    <h2 className="mt-1 text-xl font-bold text-white">{title}</h2>
                    <p className="mt-2 text-zinc-400 leading-relaxed">{body}</p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* The loop */}
        <section className="border-t border-zinc-900 bg-zinc-950/40">
          <div className="max-w-3xl mx-auto px-5 py-16">
            <p className="text-[11px] uppercase tracking-[0.2em] font-black text-[#BEFF38]">The re-loop</p>
            <h2 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">
              It doesn't stop after step 06
            </h2>
            <p className="mt-3 text-zinc-400 leading-relaxed">
              Static plans age out in three weeks. Ours re-writes itself continuously against three timescales:
            </p>
            <div className="mt-8 grid sm:grid-cols-3 gap-4">
              {RELOOP.map((r) => (
                <div key={r.h} className="rounded-xl border border-zinc-800 bg-black p-5">
                  <p className="text-[11px] uppercase tracking-wider font-black text-[#ff7a1a]">{r.h}</p>
                  <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{r.p}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-5 py-16 sm:py-20">
          <div className="rounded-2xl border border-[#BEFF38]/30 bg-gradient-to-br from-[#BEFF38]/10 to-transparent p-8 sm:p-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Ready to see it adapt?</h2>
            <p className="mt-3 text-zinc-300 leading-relaxed">
              Two minutes to your first plan. No credit card. Cancel any time if it's not for you.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/my-plan"
                onClick={() => trackEvent('start_click', { source: 'how_it_works' })}
                data-testid="hiw-start-cta"
                className="inline-flex items-center gap-2 bg-[#BEFF38] text-black font-black uppercase tracking-wider text-sm px-5 py-3 rounded-lg hover:brightness-95"
              >
                Start free plan <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/features"
                data-testid="hiw-features-link"
                className="inline-flex items-center gap-2 border border-zinc-800 text-zinc-300 hover:text-white font-black uppercase tracking-wider text-sm px-5 py-3 rounded-lg"
              >
                See every capability
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
